import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download, Save, PlusCircle, HelpCircle, Crown, Upload } from 'lucide-react';
import { INITIAL_RESUME } from './constants.js';
import { toJsonResume, detectAndParse } from './jsonResume.js';
import './App.css';

import EditorPanel from './components/EditorPanel';
import PreviewPanel from './components/PreviewPanel';
import Onboarding from './components/Onboarding';
import LandingPage from './components/LandingPage';
import PasteMode from './components/PasteMode';
import AISettingsModal from './components/AISettingsModal';
import JobAnalyzer from './components/JobAnalyzer';
import JobFinder from './components/JobFinder';
import ApplicationTracker from './components/ApplicationTracker';
import TailoredResumePreview from './components/TailoredResumePreview';
import AdminPanel from './components/AdminPanel';
import RecruitmentBlast from './components/RecruitmentBlast';
import LinkedInOptimizer from './components/LinkedInOptimizer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import SaveResumeModal from './components/SaveResumeModal';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ResumeHistoryModal from './components/ResumeHistoryModal';
import ShareModal from './components/ShareModal';
import SharedResumeView from './components/SharedResumeView';
import ATSScorePanel from './components/ATSScorePanel';
import MockInterview from './components/MockInterview';
import SalaryNegotiator from './components/SalaryNegotiator';
import PortalScanner from './components/PortalScanner';
import { exportVectorPdf, canExportVectorPdf } from './services/pdfExport.js';
import { saveResumeToCloud, getUserResumes, deleteResumeFromCloud } from './services/resumeService.js';
import { trackEvent, AnalyticsEvents } from './services/analytics.js';
import { Wand2, Briefcase, LogOut, Cloud, History, LayoutDashboard, Search, Send, Linkedin, Link2, Gauge, Mic, DollarSign, Server, MoreHorizontal } from 'lucide-react';
import GuideTour from './components/GuideTour';
import ThemeToggle from './components/ui/ThemeToggle';
import DropdownMenu from './components/ui/DropdownMenu.jsx';
import AdBanner from './components/ui/AdBanner.jsx';
import { HelmetProvider } from 'react-helmet-async';
import SEO from './components/SEO.jsx';

function AppContent() {
  const { currentUser, logout } = useAuth();

  // Default SEO
  const defaultSEO = (
    <SEO
      title="Resume Architect | Free AI Resume Builder"
      description="Build professional resumes instantly with AI. Free templates, ATS optimization, and job matching."
    />
  );
  const { isPremium, hasFeature, purchaseFeature, debugSetPremium, getFeaturePrice } = useSubscription();
  const [authView, setAuthView] = useState('login'); // login, signup, forgot-password
  const [step, setStep] = useState('onboarding'); // onboarding, editor, paste-mode, job-analyzer, application-tracker
  const [publicView, setPublicView] = useState('landing'); // 'landing', 'privacy', 'terms', null
  const [resumeData, setResumeData] = useState(INITIAL_RESUME);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');

  // Handle Template Switching with Paywall
  const handleTemplateChange = (e) => {
    const template = e.target.value;
    // Allow Modern and Classic for free. Gate others.
    if (['modern', 'classic'].includes(template) || hasFeature('premium_templates')) {
      setSelectedTemplate(template);
    } else {
      // Revert select (react controlled comp issue, but alert helps)
      purchaseFeature('premium_templates');
    }
  };
  const [showToast, setShowToast] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [apiKeys, setApiKeys] = useState({
    gemini: localStorage.getItem('gemini_api_key') || '',
    openai: localStorage.getItem('openai_api_key') || '',
    anthropic: localStorage.getItem('anthropic_api_key') || '',
    groq: localStorage.getItem('groq_api_key') || '',
    deepseek: localStorage.getItem('deepseek_api_key') || ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false); // Confirmation state for "New Resume"
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    // Check if user has seen tour
    const hasSeenTour = localStorage.getItem('hasSeenTour');
    if (!hasSeenTour) {
      setRunTour(true);
    }
  }, []);

  const [mobileTab, setMobileTab] = useState('editor'); // 'editor' or 'preview'

  const [tailoringAnalysis, setTailoringAnalysis] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [savedResumes, setSavedResumes] = useState([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Detect ?share=<id> in URL — render read-only viewer regardless of auth.
  const shareIdFromUrl = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('share');
  })();

  useEffect(() => {
    const saved = localStorage.getItem('resumeData');
    if (saved) {
      try {
        setResumeData(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load resume data", e);
      }
    }
  }, []);

  // -- Cloud Resume History --
  const handleStartFresh = () => {
    trackEvent(AnalyticsEvents.CREATE_NEW_RESUME);
    setResumeData(INITIAL_RESUME);
    localStorage.removeItem('resumeData');
    setStep('editor');
  };

  const handleSaveToCloud = () => {
    if (!currentUser) {
      showNotification("Please sign in to save resumes to the cloud.");
      return;
    }
    setIsSaveModalOpen(true);
  };

  const handleConfirmSave = async (title) => {
    try {
      await saveResumeToCloud(currentUser.uid, resumeData, title);
      trackEvent(AnalyticsEvents.SAVE_RESUME, { method: 'cloud', title });
      showNotification("Resume saved to cloud successfully!");
      setIsSaveModalOpen(false); // Close explicitly here if not handled by component
    } catch (error) {
      console.error("Cloud Save Error:", error);
      let msg = "Failed to save resume.";
      if (error.code === 'permission-denied') {
        msg = "Permission denied. Check Firestore rules.";
      } else if (error.message) {
        msg = `Error: ${error.message}`;
      }
      showNotification(msg);
    }
  };

  const handleOpenHistory = async () => {
    if (!currentUser) {
      showNotification("Please sign in to view saved resumes.");
      return;
    }

    setIsHistoryModalOpen(true);
    try {
      const resumes = await getUserResumes(currentUser.uid);
      setSavedResumes(resumes);
    } catch (error) {
      console.error("Fetch Error:", error);
      showNotification(`Failed to fetch resumes: ${error.message}`);
    }
  };

  const handleLoadResume = (resume) => {
    // Confirmation is handled in the UI/Modal now explicitly if needed, or we trust the user.
    // Removing window.confirm to fix crashing/flashing.
    try {
      const parsedData = JSON.parse(resume.data);
      setResumeData(parsedData);
      setIsHistoryModalOpen(false);
      setStep('editor');
      showNotification("Resume loaded!");
    } catch (e) {
      showNotification("Error loading resume data.");
    }
  };

  const handleDeleteResume = async (resumeId) => {
    try {
      await deleteResumeFromCloud(resumeId);
      setSavedResumes(prev => prev.filter(r => r.id !== resumeId));
      showNotification("Resume deleted.");
    } catch (error) {
      showNotification("Failed to delete resume.");
    }
  };

  // -- Drag and Drop Logic --
  const handleDragStart = (e, text) => {
    e.dataTransfer.setData("text/plain", text);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDropText = (e, field, section, index = null) => {
    const text = e.dataTransfer.getData("text/plain");

    if (section === 'personal') {
      setResumeData(prev => ({
        ...prev,
        personal: { ...prev.personal, [field]: prev.personal[field] + (prev.personal[field] ? " " : "") + text }
      }));
    } else if (section === 'experience' && index !== null) {
      const newExp = [...resumeData.experience];
      newExp[index][field] = newExp[index][field] + (newExp[index][field] ? " " : "") + text;
      setResumeData({ ...resumeData, experience: newExp });
    }

    showNotification("Text added!");
  };

  const showNotification = (msg) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(false), 2000);
  };

  // -- Handlers --

  // Print-CSS export. Also the safety net when vector rendering fails, so a
  // broken template can never leave the user with no way to get a PDF out.
  const exportViaPrint = (tip = true) => {
    if (tip) showNotification("💡 Tip: Select 'Margins: None' in print settings for best results!");
    setTimeout(() => {
      const originalTitle = document.title;
      const name = resumeData.personal?.fullName ? resumeData.personal.fullName.replace(/\s+/g, '_') : 'Resume';
      const templateName = selectedTemplate.charAt(0).toUpperCase() + selectedTemplate.slice(1);

      document.title = `${name}_${templateName}`;
      window.print();

      setTimeout(() => {
        document.title = originalTitle;
      }, 500);
    }, tip ? 1000 : 300);
  };

  const handleExportPDF = async () => {
    if (isExporting) return;
    trackEvent(AnalyticsEvents.DOWNLOAD_PDF, { template: selectedTemplate });

    // Modern template uses @react-pdf/renderer — vector PDF, ATS-clean.
    // Other templates fall back to window.print() until each is ported.
    if (canExportVectorPdf(selectedTemplate)) {
      setIsExporting(true);
      showNotification("Generating PDF…");
      try {
        await exportVectorPdf(resumeData, selectedTemplate);
        showNotification("PDF downloaded!");
      } catch (e) {
        // Keep the full error in the console for diagnosis, but never dead-end
        // the user — fall through to the print dialog.
        console.error("Vector PDF export failed:", e);
        trackEvent(AnalyticsEvents.DOWNLOAD_PDF, { template: selectedTemplate, error: e?.message || 'unknown' });
        showNotification(`Vector export failed (${e?.message || 'unknown error'}). Opening print dialog instead…`);
        exportViaPrint(false);
      } finally {
        setIsExporting(false);
      }
      return;
    }

    exportViaPrint();
  };

  const handleSaveJSON = () => {
    trackEvent(AnalyticsEvents.SAVE_RESUME, { method: 'json-resume' });
    const payload = toJsonResume(resumeData);
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(payload, null, 2));
    const name = resumeData.personal?.fullName
      ? resumeData.personal.fullName.replace(/\s+/g, '_').toLowerCase()
      : 'resume';
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `${name}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
    showNotification("Exported as JSON Resume schema!");
  };

  const importFileRef = useRef(null);

  const handleImportJSON = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { data, format } = detectAndParse(ev.target.result);
        setResumeData(data);
        setStep('editor');
        trackEvent(AnalyticsEvents.SAVE_RESUME, { method: 'import', format });
        showNotification(`Imported (${format === 'json-resume' ? 'JSON Resume schema' : 'Architect format'})!`);
      } catch (err) {
        showNotification(err.message || 'Failed to import file.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // allow re-import of same file
  };

  // State Updates
  const addExperience = () => {
    setResumeData({
      ...resumeData,
      experience: [
        { id: Date.now(), role: "", company: "", location: "", startDate: "", endDate: "", description: "" },
        ...resumeData.experience
      ]
    });
  };

  const removeExperience = (id) => {
    setResumeData({
      ...resumeData,
      experience: resumeData.experience.filter(exp => exp.id !== id)
    });
  };

  const updateExperience = (id, field, value) => {
    setResumeData({
      ...resumeData,
      experience: resumeData.experience.map(exp => exp.id === id ? { ...exp, [field]: value } : exp)
    });
  };

  const addEducation = () => {
    setResumeData({
      ...resumeData,
      education: [
        { id: Date.now(), degree: "", school: "", location: "", year: "" },
        ...resumeData.education
      ]
    });
  };

  const removeEducation = (id) => {
    setResumeData({
      ...resumeData,
      education: resumeData.education.filter(edu => edu.id !== id)
    });
  };

  const updateEducation = (id, field, value) => {
    setResumeData({
      ...resumeData,
      education: resumeData.education.map(edu => edu.id === id ? { ...edu, [field]: value } : edu)
    });
  };

  const addSkill = (skillText) => {
    if (!resumeData.skills.includes(skillText)) {
      setResumeData({
        ...resumeData,
        skills: [...resumeData.skills, skillText]
      });
    }
  };

  const removeSkill = (skill) => {
    setResumeData({
      ...resumeData,
      skills: resumeData.skills.filter(s => s !== skill)
    });
  };

  const addCustomSection = (title, iconName = 'Star') => {
    const newSection = {
      id: Date.now(),
      title: title,
      icon: iconName,
      items: []
    };
    setResumeData({
      ...resumeData,
      customSections: [...resumeData.customSections, newSection],
      sectionOrder: [...resumeData.sectionOrder, newSection.id]
    });
  };

  const removeCustomSection = (id) => {
    setResumeData({
      ...resumeData,
      customSections: resumeData.customSections.filter(s => s.id !== id),
      sectionOrder: resumeData.sectionOrder.filter(sId => sId !== id)
    });
  };

  const updateCustomSection = (id, updates) => {
    setResumeData({
      ...resumeData,
      customSections: resumeData.customSections.map(s => s.id === id ? { ...s, ...updates } : s)
    });
  };

  const reorderSections = (newOrder) => {
    setResumeData({
      ...resumeData,
      sectionOrder: newOrder
    });
  };

  // -- Mock AI for Tour --
  const handleMockAI = (skill) => {
    if (!resumeData.skills.length) return;
    const targetSkill = skill || resumeData.skills[0];

    // Simulate generation delay
    setTimeout(() => {
      setResumeData(prev => ({
        ...prev,
        personal: {
          ...prev.personal,
          summary: `(AI Generated) Highly motivated and results-oriented professional with strong expertise in ${targetSkill}. Proven track record of leveraging ${targetSkill} to build scalable solutions and drive business growth. Passionate about continuous learning and staying updated with emerging technologies.`
        }
      }));
      showNotification("AI Content Generated!");
    }, 1000);
  };

  // -- Render Views --

  // 0a. Public share-link viewer (no auth required)
  if (shareIdFromUrl) {
    return <SharedResumeView shareId={shareIdFromUrl} />;
  }

  // 0. Public Views (Landing, Privacy, Terms)
  if (publicView === 'landing') {
    return (
      <LandingPage
        onStart={() => setPublicView(null)}
        onNavigate={setPublicView}
        currentUser={currentUser}
        onLoadResume={(resume) => {
          handleLoadResume(resume);
          setPublicView(null); // Ensure we exit landing page
        }}
      />
    );
  }
  if (publicView === 'privacy') {
    return <PrivacyPolicy onBack={() => setPublicView('landing')} />;
  }
  if (publicView === 'terms') {
    return <TermsOfService onBack={() => setPublicView('landing')} />;
  }
  if (publicView === 'admin') {
    return <AdminPanel onBack={() => setPublicView('landing')} />;
  }

  // 1. Authentication Views
  if (!currentUser) {
    if (authView === 'login') {
      return <Login onSwitchToSignup={() => setAuthView('signup')} onForgotPassword={() => setAuthView('forgot-password')} />;
    }
    if (authView === 'signup') {
      return <Signup onSwitchToLogin={() => setAuthView('login')} />;
    }
    if (authView === 'forgot-password') {
      return <ForgotPassword onBack={() => setAuthView('login')} />;
    }
  }

  // 2. Paste Mode Views
  if (step === 'paste-mode') {
    return (
      <PasteMode
        setStep={setStep}
        setResumeData={setResumeData}
        showNotification={showNotification}
        apiConfig={apiKeys}
      />
    );
  }

  // 3. Onboarding
  if (step === 'onboarding') {
    return (
      <Onboarding
        setStep={setStep}
        setResumeData={setResumeData}
        showNotification={showNotification}
        onStartFresh={handleStartFresh}
      />
    );
  }

  const handleTailorResume = (analysis) => {
    setTailoringAnalysis(analysis);
    setStep('tailored-preview');
  };

  const handleSaveTailored = async (data, title) => {
    if (!currentUser) {
      showNotification("Please sign in to save resumes.");
      return;
    }
    try {
      await saveResumeToCloud(currentUser.uid, data, title);
      showNotification("Tailored resume saved to your Dashboard!");
    } catch (error) {
      console.error("Save Error:", error);
      showNotification("Failed to save resume.");
    }
  };

  if (step === 'tailored-preview' && tailoringAnalysis) {
    return (
      <TailoredResumePreview
        originalResumeData={resumeData}
        jobAnalysis={tailoringAnalysis}
        onBack={() => setStep('editor')}
        onSave={handleSaveTailored}
      />
    );
  }

  if (step === 'job-analyzer') {
    return (
      <JobAnalyzer
        resumeData={resumeData}
        apiConfig={apiKeys}
        openAIModal={() => setIsAIModalOpen(true)}
        onBack={() => setStep('editor')}
        onSaveSuccess={() => setStep('application-tracker')}
        onTailor={handleTailorResume}
        initialJob={selectedJob}
      />
    );
  }

  if (step === 'application-tracker') {
    return (
      <ApplicationTracker
        onBack={() => setStep('editor')}
        onAnalyzeNew={() => {
          setSelectedJob(null);
          setStep('job-analyzer');
        }}
        onTailor={handleTailorResume}
        onEdit={(job) => {
          setSelectedJob(job);
          setStep('job-analyzer');
        }}
        currentResumeData={resumeData}
        currentTemplate={selectedTemplate}
        showNotification={showNotification}
      />
    );
  }

  if (step === 'job-finder') {
    return (
      <JobFinder
        resumeData={resumeData}
        apiConfig={apiKeys}
        onBack={() => setStep('editor')}
        onAnalyzeJob={(job) => {
          setSelectedJob(job);
          setStep('job-analyzer');
        }}
      />
    );
  }

  if (step === 'linkedin-optimizer') {
    return <LinkedInOptimizer resumeData={resumeData} onBack={() => setStep('editor')} />;
  }

  if (step === 'recruitment-blast') {
    return <RecruitmentBlast onBack={() => setStep('editor')} />;
  }

  if (step === 'ats-score') {
    return <ATSScorePanel resumeData={resumeData} onBack={() => setStep('editor')} />;
  }

  if (step === 'salary-negotiator') {
    return (
      <SalaryNegotiator
        resumeData={resumeData}
        apiConfig={apiKeys}
        onBack={() => setStep('editor')}
        showNotification={showNotification}
      />
    );
  }

  if (step === 'portal-scanner') {
    return (
      <PortalScanner
        onBack={() => setStep('editor')}
        showNotification={showNotification}
        onAnalyzeJob={(job) => {
          setSelectedJob(job);
          setStep('job-analyzer');
        }}
      />
    );
  }

  if (step === 'mock-interview') {
    return (
      <MockInterview
        resumeData={resumeData}
        apiConfig={apiKeys}
        onBack={() => setStep('editor')}
        openAIModal={() => setIsAIModalOpen(true)}
      />
    );
  }

  // 6. Main App (Editor + Preview)

  // Runs `action` when the feature is owned, otherwise opens the paywall.
  const gated = (feature, action) => () => {
    if (hasFeature(feature)) action();
    else purchaseFeature(feature);
  };
  const priceSuffix = (feature) => (hasFeature(feature) ? '' : ` ($${getFeaturePrice(feature)})`);
  const hasAnyApiKey = Boolean(apiKeys.gemini || apiKeys.openai || apiKeys.anthropic || apiKeys.groq || apiKeys.deepseek);

  // Secondary workspaces. Rendered as a wrapping chip row so the toolbar can
  // never push the primary actions (notably Export PDF) off-screen.
  const toolChips = [
    { id: 'nav-ai', label: hasAnyApiKey ? 'AI Active' : 'Setup AI', Icon: Wand2, tone: 'text-purple-600 bg-purple-50 hover:bg-purple-100', onClick: () => setIsAIModalOpen(true) },
    { id: 'nav-job-finder', label: 'Find Jobs', Icon: Search, tone: 'text-orange-600 bg-orange-50 hover:bg-orange-100', onClick: () => setStep('job-finder') },
    { id: 'nav-analyze', label: `${hasFeature('job_analyzer') ? 'Job Analyzer' : 'Analyzer'}${priceSuffix('job_analyzer')}`, Icon: Briefcase, tone: 'text-blue-600 bg-blue-50 hover:bg-blue-100', onClick: gated('job_analyzer', () => { setSelectedJob(null); setStep('job-analyzer'); }) },
    { id: 'nav-portal-scanner', label: 'Scan ATS', Icon: Server, tone: 'text-cyan-700 bg-cyan-50 hover:bg-cyan-100', title: "Scan a company's ATS (Greenhouse, Lever, Ashby, Workable) directly", onClick: () => setStep('portal-scanner') },
    { id: 'nav-linkedin', label: `LinkedIn${priceSuffix('linkedin_optimizer')}`, Icon: Linkedin, tone: 'text-blue-700 bg-blue-100 hover:bg-blue-200', onClick: gated('linkedin_optimizer', () => setStep('linkedin-optimizer')) },
    { id: 'nav-blast', label: 'Blast', Icon: Send, tone: 'text-pink-600 bg-pink-50 hover:bg-pink-100', onClick: () => setStep('recruitment-blast') },
    { id: 'nav-ats-score', label: 'ATS Score', Icon: Gauge, tone: 'text-teal-600 bg-teal-50 hover:bg-teal-100', title: 'Live ATS score (no API key needed)', onClick: () => setStep('ats-score') },
    { id: 'nav-interview', label: `Interview${priceSuffix('mock_interview')}`, Icon: Mic, tone: 'text-purple-700 bg-purple-100 hover:bg-purple-200', title: 'Mock Interview with 6 personas', onClick: gated('mock_interview', () => setStep('mock-interview')) },
    { id: 'nav-negotiate', label: `Negotiate${priceSuffix('salary_negotiation')}`, Icon: DollarSign, tone: 'text-emerald-700 bg-emerald-100 hover:bg-emerald-200', title: 'Salary negotiation playbook', onClick: gated('salary_negotiation', () => setStep('salary-negotiator')) },
    { id: 'nav-tracker', label: 'Tracker', Icon: LayoutDashboard, tone: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100', onClick: () => setStep('application-tracker') }
  ];

  // Everything that used to crowd the top row and is not needed at a glance.
  const overflowMenuItems = [
    {
      id: 'nav-new',
      label: confirmNew ? 'Confirm new resume?' : 'New resume',
      icon: confirmNew ? <Briefcase size={16} /> : <PlusCircle size={16} />,
      danger: confirmNew,
      onClick: () => {
        if (confirmNew) {
          setStep('onboarding');
          setConfirmNew(false);
        } else {
          setConfirmNew(true);
          setTimeout(() => setConfirmNew(false), 3000);
        }
      }
    },
    { id: 'nav-import', label: 'Import resume file', icon: <Upload size={16} />, hint: 'JSON', onClick: () => importFileRef.current?.click() },
    { id: 'nav-export-json', label: 'Export as JSON Resume', icon: <Save size={16} />, onClick: handleSaveJSON },
    { separator: true },
    {
      id: 'nav-share',
      label: 'Public share link',
      icon: <Link2 size={16} />,
      onClick: () => {
        if (!currentUser) {
          showNotification('Please sign in to share.');
          return;
        }
        trackEvent(AnalyticsEvents.SHARE_LINK);
        setIsShareModalOpen(true);
      }
    },
    { separator: true },
    { id: 'nav-logout', label: 'Sign out', icon: <LogOut size={16} />, danger: true, onClick: () => logout() }
  ];

  return (
    <div className="min-h-screen bg-slate-100 font-sans flex flex-col h-screen overflow-hidden app-root">
      {defaultSEO}
      {/* Global Ad Banner (Top of App) */}
      {!isPremium && (
        <div className="shrink-0 bg-slate-50 border-b border-slate-200 no-print">
          <AdBanner slotId={process.env.REACT_APP_ADSENSE_SLOT_ID || "1234567890"} style={{ margin: '0 auto', maxWidth: '728px', padding: '0.5rem 0' }} responsive={false} />
        </div>
      )}

      {/* Top Navigation */}
      <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-3 shrink-0 z-20 no-print">
        {/* Row 1: identity + primary actions. Both groups are shrink-safe so the
            export button can never be pushed outside the viewport. */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              id="nav-editor"
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity shrink-0"
              onClick={() => {
                setPublicView('landing');
              }}
            >
              <div className="bg-blue-600 p-1.5 rounded-lg">
                <FileText className="text-white" size={20} />
              </div>
            </div>
            <h1 className="font-bold text-slate-800 hidden lg:block truncate">Resume Architect</h1>
            {isPremium && (
              <div className="flex items-center gap-1 bg-gradient-to-r from-amber-200 to-yellow-400 text-amber-900 border border-amber-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm shrink-0">
                <Crown size={12} fill="currentColor" />
                PRO
              </div>
            )}
            {/* Mobile View Switcher */}
            <div className="flex md:hidden bg-slate-100 rounded-lg p-1 shrink-0">
              <button
                onClick={() => setMobileTab('editor')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mobileTab === 'editor'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                Editor
              </button>
              <button
                onClick={() => setMobileTab('preview')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mobileTab === 'preview'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                Preview
              </button>
            </div>

            {/* Dev Only: Debug Premium Toggle */}
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={() => debugSetPremium(!isPremium)}
                className={`hidden md:flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md transition-colors shrink-0 ${isPremium ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                title="Dev Only: Toggle Premium"
              >
                <Crown size={12} />
                {isPremium ? 'Debug: Remove Pro' : 'Debug: Force Pro'}
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            <ThemeToggle />

            <select
              id="nav-templates"
              value={selectedTemplate}
              onChange={handleTemplateChange}
              className="px-2 md:px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-xs md:text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 max-w-[110px] md:max-w-[150px]"
            >
              <option value="modern">Modern</option>
              <option value="classic">Classic</option>
              <option value="minimalist">Minimalist {hasFeature('premium_templates') ? '' : `($${getFeaturePrice('premium_templates')})`}</option>
              <option value="creative">Creative {hasFeature('premium_templates') ? '' : `($${getFeaturePrice('premium_templates')})`}</option>
              <option value="professional">Pro {hasFeature('premium_templates') ? '' : `($${getFeaturePrice('premium_templates')})`}</option>
            </select>

            <input
              ref={importFileRef}
              type="file"
              accept="application/json,.json"
              onChange={handleImportJSON}
              className="hidden"
            />

            <button
              id="nav-save-cloud"
              onClick={handleSaveToCloud}
              className="hidden md:flex items-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium transition-colors text-xs md:text-sm whitespace-nowrap"
              title="Save to Cloud"
            >
              <Cloud size={16} />
              <span className="hidden lg:inline">Save Cloud</span>
            </button>

            <button
              id="nav-history"
              onClick={handleOpenHistory}
              className="hidden md:flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors text-xs md:text-sm whitespace-nowrap"
              title="View History"
            >
              <History size={16} />
              <span className="hidden lg:inline">History</span>
            </button>

            <button
              id="nav-export"
              onClick={handleExportPDF}
              disabled={isExporting}
              className="flex items-center gap-2 px-3 md:px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-wait text-white rounded-lg font-medium transition-colors shadow-sm text-xs md:text-sm whitespace-nowrap"
              title="Export PDF"
            >
              <Download size={16} />
              <span>{isExporting ? 'Preparing…' : 'PDF'}</span>
            </button>

            <button
              id="nav-help"
              onClick={() => setRunTour(true)}
              className="hidden md:flex items-center gap-2 px-2.5 py-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              title="Start Tour"
            >
              <HelpCircle size={18} />
            </button>

            <DropdownMenu
              items={overflowMenuItems}
              trigger={({ open, toggle }) => (
                <button
                  onClick={toggle}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg transition-colors ${open ? 'bg-slate-200 text-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
                  title="More actions"
                >
                  <MoreHorizontal size={18} />
                </button>
              )}
            />
          </div>
        </div>

        {/* Row 2 (desktop): tool chips. Wraps instead of overflowing. */}
        <div className="hidden md:flex flex-wrap items-center gap-2 mt-2.5 pt-2.5 border-t border-slate-100">
          {toolChips.map(({ id, label, Icon, tone, title, onClick }) => (
            <button
              key={id}
              id={id}
              onClick={onClick}
              title={title || label}
              className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors whitespace-nowrap ${tone}`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>

        {/* Mobile Toolbar (2nd Row) — scrolls horizontally rather than clipping */}
        <div className="flex md:hidden items-center gap-2 mt-2 pt-2 border-t border-slate-100 overflow-x-auto [&>button]:shrink-0">
          <button
            onClick={handleSaveToCloud}
            className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md"
          >
            <Cloud size={12} /> Save
          </button>
          <button
            onClick={handleOpenHistory}
            className="flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-50 px-2 py-1 rounded-md"
          >
            <History size={12} /> History
          </button>
          <button
            onClick={() => setIsAIModalOpen(true)}
            className="flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-md"
          >
            <Wand2 size={12} /> AI
          </button>
          <button
            onClick={() => setStep('job-finder')}
            className="flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-md"
          >
            <Search size={12} /> Jobs
          </button>
          <button
            onClick={() => {
              if (hasFeature('job_analyzer')) {
                setStep('job-analyzer');
                setSelectedJob(null);
              } else {
                purchaseFeature('job_analyzer');
              }
            }}
            className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md"
          >
            <Briefcase size={12} /> {hasFeature('job_analyzer') ? 'Analyze' : `$${getFeaturePrice('job_analyzer')}`}
          </button>
          <button
            onClick={() => setStep('linkedin-optimizer')}
            className="flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded-md"
          >
            <Linkedin size={12} /> LinkedIn
          </button>
          <button
            onClick={() => setStep('application-tracker')}
            className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md"
          >
            <LayoutDashboard size={12} /> Tracker
          </button>
          <button
            onClick={() => logout()}
            className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-md"
          >
            <LogOut size={12} />
          </button>
        </div>
      </header >

      {/* Main Workspace */}
      <main className="flex-1 flex overflow-hidden main-container relative">
        <div className={`flex-1 min-w-0 flex flex-col overflow-hidden transition-all duration-300 no-print ${mobileTab === 'preview' ? 'hidden md:flex' : 'flex'}`}>
          <EditorPanel
            resumeData={resumeData}
            setResumeData={setResumeData}
            handleDropText={handleDropText}
            addExperience={addExperience}
            removeExperience={removeExperience}
            updateExperience={updateExperience}
            addEducation={addEducation}
            removeEducation={removeEducation}
            updateEducation={updateEducation}
            addSkill={addSkill}
            removeSkill={removeSkill}
            addCustomSection={addCustomSection}
            removeCustomSection={removeCustomSection}
            updateCustomSection={updateCustomSection}
            reorderSections={reorderSections}
            handleDragStart={handleDragStart}
            apiConfig={apiKeys}
            openAIModal={() => setIsAIModalOpen(true)}
            showNotification={showNotification}
          />
        </div>
        <div id="nav-preview" className={`flex-1 min-w-0 bg-slate-500 overflow-hidden transition-all duration-300 ${mobileTab === 'editor' ? 'hidden md:flex' : 'flex'}`}>
          <PreviewPanel resumeData={resumeData} selectedTemplate={selectedTemplate} />
        </div>
      </main>

      {/* Toast Notification */}
      {
        showToast && (
          <div className="fixed bottom-6 right-6 bg-slate-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm animate-fade-in z-50">
            {showToast}
          </div>
        )
      }
      {/* Save Resume Modal */}
      <SaveResumeModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={handleConfirmSave}
        currentTitle={resumeData.personal?.fullName ? `${resumeData.personal.fullName}'s Resume` : 'My Resume'}
      />

      {/* AI Settings Modal */}
      <AISettingsModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onSave={setApiKeys}
        showNotification={showNotification}
      />

      {/* Mock AI for Tour */}
      <GuideTour run={runTour} setRun={setRunTour} onMockAI={handleMockAI} />

      {/* Resume History Modal */}
      <ResumeHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        resumes={savedResumes}
        onLoad={handleLoadResume}
        onDelete={handleDeleteResume}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        currentUser={currentUser}
        resumeData={resumeData}
        selectedTemplate={selectedTemplate}
        showNotification={showNotification}
      />
    </div >
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}